import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import {
  useCreateCustomReportTypeMutation,
  useUpdateCustomReportTypeMutation,
} from "~/store/mystore/amc.redux";

import { ALERT_CARD_TYPE } from "~/components/alert/alertCardV2";
import AutoSizedButton from "~/components/buttons/autoSizedButton";
import { BaseDialog } from "~/components/dialogs/baseDialog";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/DescriptionOutlined";
import GenericSvgIconWrapper from "~/icons/genericSvgIconWrapper";
import InfoAlertV2 from "~/components/alert/infoAlertV2";
import { PrunedAMCReportType } from "~/typedef/amc/reportType";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

const DialogHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  padding: 1.5rem 1.5rem 0 1.5rem;
`;

const HeaderLeft = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeaderIcon = styled(DescriptionIcon)`
  color: ${({ theme }) => theme.palette.secondary.main};
  fill: ${({ theme }) => theme.palette.secondary.main};
  font-size: 2rem;
  margin-bottom: 1.5rem;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 1.5rem 1.5rem 0 1.5rem;
  overflow: visible;
`;

const StyledDialogActions = styled(DialogActions)`
  padding: 1.5rem;
  border: 0;
`;

interface SaveAMLReportDialogProps {
  open: boolean;
  onClose: () => void;
  mid: string;
  query: string;
  isCodeEdited: boolean;
  reportTypeToEdit?: PrunedAMCReportType;
}

const SaveAMLReportDialog: React.FC<SaveAMLReportDialogProps> = ({
  open,
  onClose,
  mid,
  query,
  isCodeEdited,
  reportTypeToEdit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [isPublic, setIsPublic] = useState<boolean>(true);

  const [reportName, setReportName] = useState("");
  const [description, setDescription] = useState("");
  const [createCustomReport, { isLoading: isCreateCustomReportLoading }] =
    useCreateCustomReportTypeMutation();
  const [updateCustomReport, { isLoading: isUpdateCustomReportLoading }] =
    useUpdateCustomReportTypeMutation();

  useEffect(() => {
    if (reportTypeToEdit) {
      setReportName(reportTypeToEdit.name);
      setDescription(reportTypeToEdit.description || "");
      setIsPublic(reportTypeToEdit.public ?? true);
    }
  }, [reportTypeToEdit]);

  const handleSave = async (redirectToSchedule = false) => {
    let reportId: string | undefined;
    if (reportTypeToEdit) {
      await updateCustomReport({
        reportId: reportTypeToEdit._id,
        updateItem: { public: isPublic, query, name: reportName, description },
      });
      reportId = reportTypeToEdit._id;
      onClose();
    } else {
      const result = await createCustomReport({
        mid,
        query,
        reportName,
        isPublic,
        description,
      }).unwrap();
      reportId = result.reportId;
      onClose();
    }
    if (redirectToSchedule && reportId) {
      history.push(
        `/mystores/amc/schedulereport?store=${mid}&reportType=${reportId}`
      );
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          maxHeight: "none",
        },
      }}
    >
      <DialogHeader>
        <HeaderLeft>
          <HeaderIcon />
          <Typography variant="h1">
            {t("amc.customReports.saveReportTitle")}
          </Typography>
        </HeaderLeft>
        <CloseButton onClick={onClose} aria-label="close">
          <GenericSvgIconWrapper
            $color={theme.palette.text.secondary}
            $width="1.5rem"
            $height="1.5rem"
          >
            <CloseIcon />
          </GenericSvgIconWrapper>
        </CloseButton>
      </DialogHeader>
      <StyledDialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label={t("amc.customReports.reportName")}
                required
                onChange={(e) => setReportName(e.target.value)}
                value={reportName}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label={t("amc.customReports.reportDescription")}
                required
                onChange={(e) => setDescription(e.target.value)}
                value={description}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle1" color="textSecondary" noWrap>
                  {t("amc.customReports.public")}
                </Typography>
              </Grid>
              <Grid item>
                <Switch
                  size="small"
                  color="secondary"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <InfoAlertV2
              message={t("amc.customReports.publicReportInfo")}
              noBorder
            />
          </Grid>
          {reportTypeToEdit && isCodeEdited && (
            <Grid item xs={12}>
              <InfoAlertV2
                message={t("amc.customReports.editedQueryAlert")}
                noBorder
                type={ALERT_CARD_TYPE.WARNING}
              />
            </Grid>
          )}
        </Grid>
      </StyledDialogContent>
      <StyledDialogActions>
        <Grid container spacing={1}>
          <Grid item>
            <AutoSizedButton
              variant="contained"
              color="secondary"
              onClick={() => handleSave(true)}
              disabled={
                !reportName ||
                !description ||
                isCreateCustomReportLoading ||
                isUpdateCustomReportLoading
              }
            >
              {t("amc.customReports.saveAndSchedule").toUpperCase()}
            </AutoSizedButton>
          </Grid>
          <Grid item>
            <AutoSizedButton
              variant="outlined"
              onClick={() => handleSave()}
              disabled={
                !reportName ||
                !description ||
                isCreateCustomReportLoading ||
                isUpdateCustomReportLoading
              }
            >
              {t("generic.save").toUpperCase()}
            </AutoSizedButton>
          </Grid>
          <Grid item>
            <AutoSizedButton variant="text" onClick={onClose}>
              {t("generic.cancelButton").toUpperCase()}
            </AutoSizedButton>
          </Grid>
        </Grid>
      </StyledDialogActions>
    </BaseDialog>
  );
};

export default SaveAMLReportDialog;
